export interface PlanningDayValue {
    day_moment: string;
    day_moment_session: string;
    field: string;
    value: string;
}

export interface PlanningDay {
    key: string;
    name: string;
    values: PlanningDayValue[]
}

/**
 * Represents a race/competition scheduled within a planning week
 * Used in the Timeline section of the planning table to track competitions
 *
 * @property id - Unique identifier (optional, set by backend after creation)
 * @property planning_week_id - Foreign key to the planning week this race belongs to
 * @property title - Name/title of the race (e.g., "National Championship")
 * @property description - Optional additional details about the race
 */
export interface PlanningRace {
    id?: number;
    planning_week_id: number;
    title: string;
    description?: string;
}

/**
 * Represents a week in the planning system
 * Contains training days, workload data, and associated races
 *
 * @property id - Database identifier for the week (used for race associations)
 * @property key - Week key in ISO format (e.g., "2024-W42")
 * @property races - Optional array of races scheduled for this week
 */
export interface PlanningWeek {
    id?: number;
    key: string;
    name: string;
    from: string;
    from_name: string;
    to: string;
    to_name: string;
    workload: number;
    total_sessions?: number;
    days: PlanningDay[];
    races?: PlanningRace[];
    content?: PlanningWeekContent[];
}

export interface PlanningMonth {
    key: string;
    name: string;
    from: string;
    to: string;
    weeks: PlanningWeek[];
}

export interface Planning {
    months: PlanningMonth[];
}

export const emptyPlanning:Planning = {
    months: []
}

export interface Macrocycle {
    id?: number;
    name: string;
    color: string;
    weeks: string[];
}

export interface Period {
    id?: number;
    name: string;
    color: string;
    weeks: string[];
}

export interface Mesocycle {
    id?: number;
    name: string;
    color: string;
    weeks: string[];
    primary_objective?: string;
    mesocycle_objectives?: string;
}

export interface PlanningDayFeedback {
    id: number;
    athlete_id: number;
    day: string;
    when: string;
    tiredness_ratio: number;
    observations: string;
}

export interface PlanningReportingMonitoring {
    event_time: string;
    event_type: string;
    event_value: string;
}

export interface PlanningReportingAthlete {
    athlete_id: number;
    athlete_name: string;
    duration: number;
    fatigue_level: number;
    observations: string;
}

export interface PlanningMonitoringMeta {
    sample_shovelfuls: number;
    observations: string;
}

export interface PlanningMonitoringAthlete {
    athlete_id: number;
    athlete_name: string;
}

export interface PlanningReportingDaySession {
    day_moment_session: string;
    avg_duration: number;
    avg_fatigue_level: number;
    avg_speed_avg: number;
    avg_temperature: number;
    avg_wind_speed: number;
    has_data: number;
    athletes: PlanningReportingAthlete[];
    monitoring: PlanningReportingMonitoring[];
    monitoring_meta?: PlanningMonitoringMeta | null;
    monitoring_athletes?: PlanningMonitoringAthlete[];
}

export const emptyPlanningReportingDaySession: PlanningReportingDaySession = {
    day_moment_session: '',
    avg_duration: 0,
    avg_fatigue_level: 0,
    avg_speed_avg: 0,
    avg_temperature: 0,
    avg_wind_speed: 0,
    has_data: 0,
    athletes: [],
    monitoring: [],
    monitoring_meta: null,
    monitoring_athletes: []
}

export interface PlanningReportingDayMoment {
    day_moment: string;
    avg_duration: number;
    avg_fatigue_level: number;
    avg_speed_avg: number;
    avg_temperature: number;
    avg_wind_speed: number;
    has_data: number;
    day_moment_session: PlanningReportingDaySession[];
}

export const emptyPlanningReportingDayMoment: PlanningReportingDayMoment = {
    day_moment: '',
    avg_duration: 0,
    avg_fatigue_level: 0,
    avg_speed_avg: 0,
    avg_temperature: 0,
    avg_wind_speed: 0,
    has_data: 0,
    day_moment_session: []
}

export interface PlanningReportingDay {
    day: string;
    avg_duration: number;
    avg_fatigue_level: number;
    avg_speed_avg: number;
    avg_temperature: number;
    avg_wind_speed: number;
    has_data: number;
    day_moment: PlanningReportingDayMoment[];
}

export interface PlanningReportingWeek {
    week: string;
    week_num: number;
    workload: number;
    avg_duration: number;
    avg_fatigue_level: number;
    avg_speed_avg: number;
    avg_temperature: number;
    avg_wind_speed: number;
    has_data: number;
    days: PlanningReportingDay[];
}

export interface PlanningReportingMonth {
    month: string;
    workload: number;
    avg_duration: number;
    avg_fatigue_level: number;
    avg_speed_avg: number;
    avg_temperature: number;
    avg_wind_speed: number;
    has_data: number;
    weeks: PlanningReportingWeek[];
}

export interface PlanningReporting {
    avg_duration: number;
    avg_fatigue_level: number;
    avg_speed_avg: number;
    avg_temperature: number;
    avg_wind_speed: number;
    has_data: number;
    months: PlanningReportingMonth[];
}

export const emptyPlanningReporting: PlanningReporting = {
    avg_duration: 0,
    avg_fatigue_level: 0,
    avg_speed_avg: 0,
    avg_temperature: 0,
    avg_wind_speed: 0,
    has_data: 0,
    months: []
}

export const planningLevels = [
    {
        key: 'R0',
        name: 'R0',
        type: 'water'
    },
    {
        key: 'R1',
        name: 'R1',
        type: 'water'
    },
    /*{
        key: 'R1+',
        name: 'R1+',
        type: 'water'
    },*/
    {
        key: 'R2',
        name: 'R2',
        type: 'water'
    },
    /*{
        key: 'R2+',
        name: 'R2+',
        type: 'water'
    },*/
    {
        key: 'R3',
        name: 'R3',
        type: 'water'
    },
    {
        key: 'R3+',
        name: 'R3+',
        type: 'water'
    },
    {
        key: 'R4',
        name: 'R4',
        type: 'water'
    },
    {
        key: 'R5',
        name: 'R5',
        type: 'water'
    },
    {
        key: 'R6',
        name: 'R6',
        type: 'water'
    },
    {
        key: 'R7',
        name: 'R7',
        type: 'water'
    },
    {
        key: 'R2-3F',
        name: 'R2-3F',
        type: 'water'
    },
    {
        key: 'R5F',
        name: 'R5F',
        type: 'water'
    },
    {
        key: 'FH',
        name: 'FH',
        type: 'ground'
    },
    {
        key: 'FX',
        name: 'FX',
        type: 'ground'
    },
    {
        key: 'FC',
        name: 'FC',
        type: 'ground'
    },
    {
        key: 'FV',
        name: 'FV',
        type: 'ground'
    },
    {
        key: 'FG',
        name: 'FG',
        type: 'ground'
    },
    {
        key: 'RG',
        name: 'RG',
        type: 'ground'
    },
    {
        key: 'RV',
        name: 'RV',
        type: 'ground'
    },
    /*{
        key: '2',
        name: '2',
        type: 'ground'
    },
    {
        key: '3',
        name: '3',
        type: 'ground'
    },
    {
        key: '4',
        name: '4',
        type: 'ground'
    },
    {
        key: '5',
        name: '5'
    },
    {
        key: '6',
        name: '6',
        type: 'ground'
    },
    {
        key: 'K4',
        name: 'K4',
        type: 'ground'
    },
    {
        key: 'K2',
        name: 'K2',
        type: 'ground'
    }*/
]

/**
 * Content distribution fields configuration
 * These fields represent different content areas for planning weeks
 */
export const contentDistributionFields = [
    {
        key: 'tecnica',
        name: 'Técnica'
    },
    {
        key: 'tactica',
        name: 'Táctica'
    },
    {
        key: 'fisica',
        name: 'Física'
    },
    {
        key: 'psicologica',
        name: 'Psicológica'
    }
]

/**
 * Planning Week Content interface
 * Represents a content entry for a specific field in a planning week
 */
export interface PlanningWeekContent {
    id: number;
    planning_week_id: number;
    field: string;
    value: string;
    color: string;
    observations: string;
    created_by?: number;
    created_at?: string;
    changed_by?: number;
    changed_at?: string;
}

export const emptyPlanningWeekContent: PlanningWeekContent = {
    id: 0,
    planning_week_id: 0,
    field: '',
    value: '',
    color: '#ffffff',
    observations: ''
}